import { useState } from 'react';
import DefaultLayout from 'admin/components/DefaultLayout';
import { useIntl } from 'react-intl';
import {
  Tabs,
  Table,
  Button,
  Select,
  SelectOption,
  Spinner,
} from 'ui';
import { Form, message } from 'antd';
import useQueryApiClient from 'utils/useQueryApiClient';
import { useForm } from 'antd/lib/form/Form';
import { StyledTable } from './style';
import { useAuth } from '../../../hooks/useAuth';
import { Product, QualityMarkProduct } from 'interfaces/BackendModels';

const QualityMarksSync = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [products, setProducts] = useState<Product[] | null>(null);

  const intl = useIntl();
  const [form] = useForm();
  const { user } = useAuth();

  const farmId = user?.farmId;

  useQueryApiClient({
    request: {
      url: 'api/v2/qmark/farms/' + farmId + '/products',
      data: {
        page: 1,
        page_size: 100,
        filter: {
          farm: farmId
        }
      },
    },
    onSuccess: (response) => {
      setProducts(response);
      setIsLoading(false);
    },
    onError: () => {
      setIsLoading(false);
    },
  });

  const { appendData: submitData } = useQueryApiClient({
    request: {
      url: 'api/v2/qmark/farms/' + farmId + '/qualityProducts',
      method: 'POST',
    },
    onSuccess: () =>
      message.success(
        intl.formatMessage({ id: 'quality_marks_mapping.save_success' })
      ),
    onError: () => {
      message.error(intl.formatMessage({ id: 'quality_marks_mapping.save_failed' }));
    },
  });

  const columns = [
    {
      title: intl.formatMessage({ id: 'quality_marks_mapping.title' }),
      render: (qualityMarkProduct: QualityMarkProduct) => {
        return (
          <>
            <div className={'bold'}>{qualityMarkProduct.quality_mark.name}</div>
            <div>{qualityMarkProduct.registry_name}</div>
          </>
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'quality_marks_mapping.product_title' }),
      render: (qualityMarkProduct: QualityMarkProduct) => {
        const initialValueProps =
          qualityMarkProduct.product_id != null
            ? { initialValue: qualityMarkProduct.product_id }
            : {};
        return (
          <Select
            key={qualityMarkProduct.id}
            name={qualityMarkProduct.id}
            {...initialValueProps}
            allowClear
          >
            {products?.map((product: Product) => (
              <SelectOption key={product.id} value={product.id}>
                {product.name}
              </SelectOption>
            ))}
          </Select>
        );
      },
    },
  ];

  const items = [
    {
      key: 'quality_marks',
      label: intl.formatMessage({ id: 'quality_marks_mapping.needs_sync' }),
      children: (
        <StyledTable>
          <Table
            url={'api/v2/qmark/farms/' + farmId + '/qualityProducts'}
            columns={columns}
            disablePagination
          />
        </StyledTable>
      ),
    },
  ];

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      const values = { mapping: form.getFieldsValue() };

      submitData(values, { farmId: farmId });
    } catch (e) {
      console.error(e);
    }
  };

  const Footer = () => {
    return (
      <div className={'footer'}>
        <Button label={intl.formatMessage({ id: 'general.cancel' })} />
        <Button
          label={intl.formatMessage({ id: 'general.save' })}
          onClick={handleSubmit}
          type={'primary'}
        />
      </div>
    );
  };

  return (
    <div>
      <DefaultLayout.PageLayout
        breadcrumbs={[
          {
            title: intl.formatMessage({ id: 'navigation.beginning' }),
          },
          {
            title: intl.formatMessage({ id: 'navigation.quality_marks' }),
          },
        ]}
      >
        <DefaultLayout.PageContent>
          <Spinner spinning={isLoading}>
            <Form form={form} layout={'vertical'}>
              <div>{intl.formatMessage({ id: 'quality_marks_mapping.info-message' })}</div>
              <Tabs items={items} />
            </Form>
          </Spinner>
        </DefaultLayout.PageContent>
      </DefaultLayout.PageLayout>
      <DefaultLayout.PageFooter>
        <Footer />
      </DefaultLayout.PageFooter>
    </div>
  );
};

export default QualityMarksSync;
